import { ListResult } from '@lemoncloud/codes-backend-api/dist/cores/types';
import { ProductOrderBody, ProductView, SettingSetView } from '@lemoncloud/codes-goods-api';
import { Params } from '@lemoncloud/lemon-web-core';

import { webCore } from '@codes/web-core';

import {
    ApproveProductDTO,
    AutoDeployProductDTO,
    CreateProductBody,
    UpdateProductBody,
    UpdateProductSettingsDTO,
} from '../types';

const BACKEND_ENDPOINT = import.meta.env.VITE_BACKEND_ENDPOINT.toLowerCase();

export const orderCatalogs = async (body: ProductOrderBody): Promise<ProductView> => {
    if (!body?.projectId) {
        throw new Error('orderCatalogs: @projectId is required');
    }

    if (!body?.catalogId) {
        throw new Error('orderCatalogs: @catalogId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/products/0/order`,
        })
        .setBody(body)
        .execute<ProductView>();

    return data;
};

export const fetchProductsByProjectId = async (project: string, params: Params): Promise<ListResult<ProductView>> => {
    if (!project) {
        throw new Error('fetchProductsByProjectId: .project is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/products/0/list`,
        })
        .setParams({ ...params })
        .setBody({ projectId: project })
        .execute<ListResult<ProductView>>();

    return { ...data };
};

export const fetchProducts = async (params: Params): Promise<ListResult<ProductView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/products/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<ProductView>>();

    return { ...data };
};

export const fetchProductById = async (productId?: string): Promise<ProductView> => {
    if (!productId) {
        throw new Error('fetchProductById: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}/detail`,
        })
        .execute<ProductView>();

    return { ...data };
};

export const fetchProductSettingsById = async (productId?: string): Promise<SettingSetView[]> => {
    if (!productId) {
        throw new Error('fetchProductSettingsById: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}/settings`,
        })
        .setParams({
            config: 1,
            environ: 1,
        })
        .execute<SettingSetView[]>();

    return data;
};

export const updateProductSettings = async (
    updateProductSettingsDTO: UpdateProductSettingsDTO
): Promise<ProductView> => {
    if (!updateProductSettingsDTO.productId) {
        throw new Error('updateProductSettings: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/products/${updateProductSettingsDTO.productId}/settings`,
        })
        .setBody(updateProductSettingsDTO.body)
        .execute<ProductView>();

    return data;
};

export const createProduct = async (createProduct: CreateProductBody): Promise<ProductView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BACKEND_ENDPOINT}/products/0`,
        })
        .setBody({ ...createProduct.body })
        .execute<ProductView>();

    return data;
};

export const updateProduct = async (updateProductBody: UpdateProductBody): Promise<ProductView> => {
    const { productId, body } = updateProductBody;
    if (!productId) {
        throw new Error('updateProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}`,
        })
        .setBody({ ...body })
        .execute<ProductView>();

    return data;
};

export const updateProductAtAdmin = async (updateProductBody: UpdateProductBody): Promise<ProductView> => {
    const { productId, body } = updateProductBody;
    if (!productId) {
        throw new Error('updateProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}/admin`,
        })
        .setBody({ ...body })
        .execute<ProductView>();

    return data;
};

export const deleteProduct = async (productId: string): Promise<ProductView> => {
    if (!productId) {
        throw new Error('deleteProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}`,
        })
        .setBody({})
        .setParams({ async: 1, force: 1 })
        .execute<ProductView>();

    return data;
};

export const autoDeployProduct = async (dto: AutoDeployProductDTO): Promise<ProductView> => {
    if (!dto.productId) {
        throw new Error('autoDeployProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${BACKEND_ENDPOINT}/products/${dto.productId}/deploy-step` })
        .setParams({ async: 1, ...dto.params })
        .setBody({})
        .execute<ProductView>();

    return data;
};

export const approveProduct = async (dto: ApproveProductDTO): Promise<ProductView> => {
    if (!dto.productId) {
        throw new Error('approveProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${BACKEND_ENDPOINT}/products/${dto.productId}/approval` })
        .setBody({})
        .execute<ProductView>();

    return data;
};

export const cancelProduct = async (productId: string): Promise<ProductView> => {
    if (!productId) {
        throw new Error('cancelProduct: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${BACKEND_ENDPOINT}/products/${productId}/cancel` })
        .setBody({ next: 'rejected' })
        .execute<ProductView>();

    return data;
};

export const fetchChildProducts = async (productId: string): Promise<ListResult<ProductView>> => {
    if (!productId) {
        throw new Error('fetchChildProducts: @productId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BACKEND_ENDPOINT}/products/${productId}/child`,
        })
        .execute<ListResult<ProductView>>();

    return data;
};
